<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { M1, M7 } from '../modules';

import type { Station } from '../types';

  export let currentScreen: string = '';
  export let onSlotClick: (station: Station) => void = () => {};
  export let catalogError: string | null = null;

  let genres: string[] = [];
  let allStations: Station[] = [];
  let selectedGenre: string | null = null;
  let activePreviewUrl: string | null = null;
  let previewTimeout: ReturnType<typeof setTimeout> | null = null;
  let filterDropdownOpen: boolean = false;
  let filterContainerEl: HTMLElement;

  $: filteredStations = selectedGenre
    ? allStations.filter((s: Station) => s.genre === selectedGenre)
    : allStations;

  $: hasActiveFilter = selectedGenre !== null;

  // Pull catalog from M7 when this screen becomes active.
  // NOTE: assignments are inlined in the $: block, NOT in a helper
  // function, because the Svelte 4 compiler (via esbuild-svelte) can
  // mis-map variable slots when mutations happen inside a closure.
  $: if (currentScreen === 'browser') {
    if (M7) {
      allStations = M7.getStations?.() ?? [];
      genres = M7.getGenres?.() ?? [];
    }
  }

  onMount(() => {
    mounted = true;
    loadCatalog();
    if (allStations.length === 0) startRetryPolling();
    return () => {
      mounted = false;
      stopRetryPolling();
    };
  });

  onDestroy(() => {
    stopRetryPolling();
  });

  let retryTimer: ReturnType<typeof setInterval> | null = null;
  let mounted = false;
  const CATALOG_RETRY_INTERVAL_MS = 500;

  function loadCatalog() {
    if (M7) {
      allStations = M7.getStations?.() ?? [];
      genres = M7.getGenres?.() ?? [];
    }
  }

  function startRetryPolling() {
    if (retryTimer) return;
    retryTimer = setInterval(() => {
      loadCatalog();
      if (allStations.length > 0) stopRetryPolling();
    }, CATALOG_RETRY_INTERVAL_MS);
  }

  function stopRetryPolling() {
    if (retryTimer) {
      clearInterval(retryTimer);
      retryTimer = null;
    }
  }

  async function retryLoad() {
    catalogError = null;
    try {
      await (window as any).M7?.init?.();
      loadCatalog();
    } catch (e: any) {
      catalogError = e?.message || 'Failed to load station catalog';
    }
  }

  $: if (currentScreen === 'browser' && allStations.length === 0 && mounted) {
    startRetryPolling();
  } else if (currentScreen !== 'browser') {
    stopRetryPolling();
  }

  function selectGenre(g: string | null) {
    selectedGenre = g;
    filterDropdownOpen = false;
  }

  function toggleFilterDropdown() {
    filterDropdownOpen = !filterDropdownOpen;
  }

  function closeFilterDropdown() {
    filterDropdownOpen = false;
  }

  function handleWindowClick(e: MouseEvent) {
    if (filterDropdownOpen && filterContainerEl && !filterContainerEl.contains(e.target as Node)) {
      closeFilterDropdown();
    }
  }

  async function togglePreview(station: any) {
    if (!M1) return;
    if (M1.isPreviewActive?.()) {
      if (activePreviewUrl === station.url) {
        M1.stopPreview?.();
        activePreviewUrl = null;
        return;
      }
    }
    await M1.startPreview?.(station.url);
    activePreviewUrl = station.url;
    if (previewTimeout) clearTimeout(previewTimeout);
    previewTimeout = setTimeout(() => {
      M1?.stopPreview?.();
      activePreviewUrl = null;
    }, 30000);
  }
</script>

<svelte:window on:click={handleWindowClick} />

<div class="sb-filter-container" bind:this={filterContainerEl}>
  <button class="sb-filter-btn" class:has-filter={hasActiveFilter}
    on:click={toggleFilterDropdown} aria-expanded={filterDropdownOpen} aria-haspopup="listbox">
    <svg class="filter-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
    </svg>
    {selectedGenre ? `[${selectedGenre}]` : 'Filter'}
  </button>
  {#if filterDropdownOpen}
    <div class="sb-filter-dropdown" role="listbox" aria-label="Genre filters">
      <div class="sb-filter-pills">
        <button class="sb-pill" class:active={selectedGenre === null}
          on:click={() => selectGenre(null)} aria-selected={selectedGenre === null}>[All]</button>
        {#each genres as g}
          <button class="sb-pill" class:active={selectedGenre === g}
            on:click={() => selectGenre(g)} aria-selected={selectedGenre === g} aria-label="Filter by genre {g}">[{g}]</button>
        {/each}
      </div>
    </div>
  {/if}
</div>

<div id="sb-card-list" class="sb-card-list">
  {#each filteredStations as st (st.url)}
    <div class="sb-card" data-url={st.url}>
      <div class="sb-card-info">
        <span class="sb-card-name">{st.name}</span>
        {#if st.description}
          <div class="sb-card-desc">{st.description}</div>
        {:else}
          <div class="sb-card-desc sb-card-desc-empty"></div>
        {/if}
        {#if st.genre}
          <div class="sb-card-genre">{st.genre}</div>
        {/if}

      </div>
      <div class="sb-card-actions">
        <button class="sb-card-btn" title="Add to roster"
          aria-label="Add {st.name} to roster"
          on:click={() => onSlotClick(st)}>[add]</button>
        <button
          class="sb-card-btn sb-preview-btn"
          class:sb-preview-active={activePreviewUrl === st.url}
          title="Preview"
          aria-label="{activePreviewUrl === st.url ? 'Stop preview' : 'Preview'} {st.name}"
          on:click={() => togglePreview(st)}>
          [try it!]
        </button>
      </div>
    </div>
  {:else}
    {#if catalogError}
      <div class="sb-error">
        <p class="screen-placeholder">{catalogError}</p>
        <button class="sb-pill" on:click={retryLoad}>[Retry]</button>
      </div>
    {:else}
      <p class="screen-placeholder">No stations available</p>
    {/if}
  {/each}
</div>
